import { Label } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { AuthContext } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import { Col, DatePicker, Input, Modal, Row, Select } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { MODULE_PAYROLL_ORDER } from '../../../constants/modules';
import { createRollingYearWindowDisabler } from '../../../services/util/date';
import { RASCUNHO_ID } from '../../../services/util/globalConstant';
import { allowOnlyDateCharacters } from '../../../services/util/input';
import { extractTextByRegex } from '../../../services/util/string';
import BwgButton from '../../atoms/Button';
import SelectFolha from '../../atoms/SelectFolha';
import SelectStatus from '../../atoms/SelectStatus';
import MultiSelect from '../MultiSelect';

const disableInvoiceDueDateOutsideAllowedWindow = createRollingYearWindowDisabler({
  startYear: 2000,
  yearsAhead: 2,
});

function PayrollSearchFilters({
  defaultFilters,
  appliedFilters,
  onApplyFiltersClick,
  onClearFiltersClick,
  carteiraList,
  canShowApdataInfo,
}) {
  const { isAdmin } = useContext(AuthContext);
  const [innerFilters, setFilters] = useState(appliedFilters);
  const isFiltersChanged = JSON.stringify(appliedFilters) !== JSON.stringify(innerFilters);

  const {
    atualizadoEmInicial,
    atualizadoEmFinal,
    dataPrevistaFaturaInicial,
    dataPrevistaFaturaFinal,
    carteiras,
    dataPagamentoCoop,
    folhas,
    idsRequisicao,
    status,
    logsTransacao,
  } = innerFilters;

  const areAllCarteirasSelected = carteiras && carteiraList && carteiras.length === carteiraList.length;
  const applyFilters = () => {
    if (atualizadoEmFinal.diff(atualizadoEmInicial, 'month') > 5) {
      return Modal.warning({
        content: 'O período selecionado na "Data da operação" deve estar limitado a 5 meses',
      });
    }
    return onApplyFiltersClick(innerFilters);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    return onClearFiltersClick(defaultFilters);
  };

  return (
    <Row
      type="flex"
      gutter={[16, 16]}
    >
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={8}
        xl={6}
        xxl={6}
      >
        <Label label="Data da operação">
          <DatePicker.RangePicker
            allowClear={false}
            placeholder={['DD/MM/YYYY', 'DD/MM/YYYY']}
            format="DD/MM/YYYY"
            picker="date"
            disabledDate={disableInvoiceDueDateOutsideAllowedWindow}
            onKeyDown={(event) => allowOnlyDateCharacters(event)}
            value={[atualizadoEmInicial, atualizadoEmFinal]}
            onChange={([begin, end]) =>
              setFilters({
                ...innerFilters,
                atualizadoEmInicial: begin,
                atualizadoEmFinal: end,
              })
            }
          />
        </Label>
      </Col>

      {isAdmin() && (
        <Col
          xs={24}
          sm={24}
          md={12}
          lg={8}
          xl={6}
          xxl={6}
        >
          <Label label="Data do Vencimento Previsto da Fatura">
            <DatePicker.RangePicker
              allowClear
              placeholder={['DD/MM/YYYY', 'DD/MM/YYYY']}
              format="DD/MM/YYYY"
              picker="date"
              disabledDate={disableInvoiceDueDateOutsideAllowedWindow}
              onKeyDown={(event) => allowOnlyDateCharacters(event)}
              value={[dataPrevistaFaturaInicial, dataPrevistaFaturaFinal]}
              onChange={(newValue) =>
                setFilters({
                  ...innerFilters,
                  dataPrevistaFaturaInicial: newValue && newValue[0],
                  dataPrevistaFaturaFinal: newValue && newValue[1],
                })
              }
            />
          </Label>
        </Col>
      )}
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={8}
        xl={6}
        xxl={6}
      >
        <Label label="Pagamento ao Cooperado">
          <DatePicker.RangePicker
            allowClear
            placeholder={['DD/MM/YYYY', 'DD/MM/YYYY']}
            format="DD/MM/YYYY"
            picker="date"
            onKeyDown={(event) => allowOnlyDateCharacters(event)}
            disabledDate={disableInvoiceDueDateOutsideAllowedWindow}
            value={[dataPagamentoCoop.inicial, dataPagamentoCoop.final]}
            onChange={(newValue) =>
              setFilters({
                ...innerFilters,
                dataPagamentoCoop: {
                  inicial: newValue && newValue[0],
                  final: newValue && newValue[1],
                },
              })
            }
          />
        </Label>
      </Col>

      <Col
        xs={24}
        sm={24}
        md={12}
        lg={8}
        xl={6}
        xxl={6}
      >
        <SelectStatus
          selectedStatusList={status}
          excluded={[RASCUNHO_ID]}
          onChange={(selectedStatusList) => setFilters({ ...innerFilters, status: selectedStatusList })}
        />
      </Col>
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={8}
        xl={6}
        xxl={6}
      >
        <Label label="Carteira Folha">
          <MultiSelect
            placeholder="Buscar pelo responsável da carteira folha"
            maxTagTextLength={5}
            maxTagCount={1}
            value={carteiras}
            areAllSelected={areAllCarteirasSelected}
            onChangeAllSelected={() => {
              if (areAllCarteirasSelected) {
                return setFilters({ ...innerFilters, carteiras: [] });
              }

              return setFilters({ ...innerFilters, carteiras: [...carteiraList] });
            }}
            onChange={(values) => setFilters({ ...innerFilters, carteiras: values })}
          >
            {carteiraList &&
              carteiraList.map((carteira) => (
                <Select.Option
                  key={`carteira-${carteira}`}
                  value={carteira}
                >
                  {carteira}
                </Select.Option>
              ))}
          </MultiSelect>
        </Label>
      </Col>
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={5}
        xl={5}
        xxl={5}
      >
        <Label label="Número da requisição">
          <Input
            onChange={(e) =>
              setFilters({ ...innerFilters, idsRequisicao: extractTextByRegex(e.target.value, /[0-9,]/g) })
            }
            type="text"
            value={idsRequisicao}
            placeholder="Buscar por número da requisição"
            allowClear
          />
        </Label>
      </Col>
      {canShowApdataInfo && (
        <Col
          xs={24}
          sm={24}
          md={12}
          lg={5}
          xl={5}
          xxl={5}
        >
          <Label label="Log transação">
            <Input
              onChange={(e) =>
                setFilters({ ...innerFilters, logsTransacao: extractTextByRegex(e.target.value, /[0-9,]/g) })
              }
              type="text"
              value={logsTransacao}
              placeholder="Buscar por log transação"
              allowClear
            />
          </Label>
        </Col>
      )}
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={6}
        xl={6}
        xxl={6}
      >
        <SelectFolha
          module={MODULE_PAYROLL_ORDER}
          folhaSelecionada={folhas}
          onChange={(value) => setFilters({ ...innerFilters, folhas: value })}
        />
      </Col>
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={4}
        xl={4}
        xxl={4}
      >
        <Label label="&nbsp;">
          <BwgButton
            type="primary"
            disabled={!isFiltersChanged}
            onClick={() => applyFilters()}
          >
            Aplicar Filtros
          </BwgButton>
        </Label>
      </Col>
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={3}
        xl={3}
        xxl={3}
      >
        <Label label="&nbsp;">
          <BwgButton
            type="link"
            onClick={clearFilters}
          >
            Limpar Filtros
          </BwgButton>
        </Label>
      </Col>
    </Row>
  );
}

const filtersShape = PropTypes.shape({
  atualizadoEmInicial: PropTypes.instanceOf(moment),
  atualizadoEmFinal: PropTypes.instanceOf(moment),
  dataPrevistaFaturaInicial: PropTypes.instanceOf(moment),
  dataPrevistaFaturaFinal: PropTypes.instanceOf(moment),
  dataPagamentoCoop: PropTypes.shape({
    inicial: PropTypes.instanceOf(moment),
    final: PropTypes.instanceOf(moment),
  }),
  folhas: PropTypes.shape(),
  carteiras: PropTypes.arrayOf(PropTypes.string),
  idsRequisicao: PropTypes.string,
  status: PropTypes.arrayOf(PropTypes.string),
});

PayrollSearchFilters.propTypes = {
  defaultFilters: filtersShape.isRequired,
  appliedFilters: filtersShape.isRequired,
  onApplyFiltersClick: PropTypes.func.isRequired,
  onClearFiltersClick: PropTypes.func.isRequired,
  carteiraList: PropTypes.arrayOf(PropTypes.string).isRequired,
  canShowApdataInfo: PropTypes.bool.isRequired,
};

export default PayrollSearchFilters;
